'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ProgramCardProps {
  program: {
    id: string;
    title: string;
    category: string;
    description: string;
    image: string;
    alt: string;
    outcomes: string[];
    ageRange: string;
    duration: string;
    studentsReached: number;
  };
  onLearnMore: (id: string) => void;
}

const ProgramCard = ({ program, onLearnMore }: ProgramCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-card rounded-xl shadow-brand-md hover:shadow-brand-lg transition-all duration-300 overflow-hidden group">
      <div className="relative h-64 overflow-hidden">
        <AppImage
          src={program.image}
          alt={program.alt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 bg-brand-primary text-brand-primary-foreground text-xs font-semibold rounded-full">
            {program.category}
          </span>
        </div>
      </div>

      <div className="p-6">
        <h3 className="text-xl font-semibold text-text-primary mb-2">
          {program.title}
        </h3>

        <p className="text-text-secondary text-sm mb-4 line-clamp-2">
          {program.description}
        </p>

        <div className="flex items-center gap-4 mb-4 text-xs text-text-secondary">
          <div className="flex items-center gap-1">
            <Icon name="UserGroupIcon" size={16} variant="outline" />
            <span>{program.ageRange}</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="ClockIcon" size={16} variant="outline" />
            <span>{program.duration}</span>
          </div>
        </div>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-brand-primary text-sm font-medium hover:text-brand-primary/80 transition-colors duration-300 mb-4"
        >
          <span>{isExpanded ? 'Show Less' : 'Key Outcomes'}</span>
          <Icon
            name="ChevronDownIcon"
            size={16}
            variant="outline"
            className={`transition-transform duration-300 ${
              isExpanded ? 'rotate-180' : ''
            }`}
          />
        </button>

        {isExpanded && (
          <div className="mb-4 space-y-2 animate-slide-in-right">
            {program.outcomes.map((outcome, index) => (
              <div key={index} className="flex items-start gap-2">
                <Icon
                  name="CheckCircleIcon"
                  size={16}
                  variant="solid"
                  className="text-success mt-0.5 flex-shrink-0"
                />
                <span className="text-sm text-text-secondary">{outcome}</span>
              </div>
            ))}
          </div>
        )}

        {!['hydroponics-in-schools', 'climate-tech-labs', 'nutrition-science'].includes(program.id) && (
          <button
            onClick={() => onLearnMore(program.id)}
            className="w-full px-4 py-2.5 bg-surface text-brand-primary font-semibold text-sm rounded-lg hover:bg-brand-primary hover:text-brand-primary-foreground transition-all duration-300"
          >
            Learn More
          </button>
        )}
      </div>
    </div>
  );
};

export default ProgramCard;